import React from 'react';
import { Typography, Tooltip } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { formatTimeForUser, getUserTimezone } from '../utils/timezone';
import { useAuth } from '../hooks/useAuth';

const { Text } = Typography;

interface TimezoneAwareTimeProps {
  /** UTC时间字符串或Date对象 */
  time: string | Date;
  /** 时间格式，默认为 'YYYY-MM-DD HH:mm:ss' */
  format?: string;
  /** 是否显示时区信息 */
  showTimezone?: boolean;
  /** 是否显示为相对时间 */
  relative?: boolean;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 是否可点击 */
  clickable?: boolean;
  /** 点击事件 */
  onClick?: () => void;
}

const TimezoneAwareTime: React.FC<TimezoneAwareTimeProps> = ({
  time,
  format = 'YYYY-MM-DD HH:mm:ss',
  showTimezone = false,
  relative = false,
  style,
  clickable = false,
  onClick
}) => {
  const { user } = useAuth();
  const userTimezone = getUserTimezone(user?.timezone);

  // 格式化时间
  const formattedTime = formatTimeForUser(time, userTimezone, format);
  
  // 获取时区显示信息
  const timezoneDisplay = showTimezone ? ` (${userTimezone})` : '';
  
  // 相对时间显示
  const getRelativeTime = () => {
    if (!time) return '';
    
    const now = new Date();
    const timeDate = new Date(time);
    const diffMs = now.getTime() - timeDate.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}天前`;
    } else if (diffHours > 0) {
      return `${diffHours}小时前`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}分钟前`;
    } else {
      return '刚刚';
    }
  };

  // 渲染内容
  const renderContent = () => {
    if (relative) {
      return (
        <span>
          {getRelativeTime()}
          {showTimezone && <span style={{ color: '#999', fontSize: '0.9em' }}>{timezoneDisplay}</span>}
        </span>
      );
    }

    return (
      <span>
        {formattedTime}
        {showTimezone && <span style={{ color: '#999', fontSize: '0.9em' }}>{timezoneDisplay}</span>}
      </span>
    );
  };

  // 工具提示内容
  const tooltipContent = (
    <div>
      <div>UTC时间: {new Date(time).toISOString()}</div>
      <div>用户时区: {userTimezone}</div>
      <div>本地时间: {formattedTime}</div>
    </div>
  );

  const timeComponent = (
    <Text
      style={{
        ...style,
        cursor: clickable ? 'pointer' : 'default',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}
      onClick={clickable ? onClick : undefined}
    >
      <ClockCircleOutlined style={{ fontSize: '14px', color: '#1890ff' }} />
      {renderContent()}
    </Text>
  );

  return (
    <Tooltip title={tooltipContent} placement="top">
      {timeComponent}
    </Tooltip>
  );
};

export default TimezoneAwareTime;
